/**
 * Moteur de calcul de paie — Luxembourg
 *
 * Pipeline conforme aux regles CCSS/ACD :
 *   1. Brut total = base + HS + 13e + avantages en nature
 *   2. Cotisations SS (maladie + pension) sur brut plafonne a 5x SSM
 *   3. Dependance (1.40%) sur assiette = brut - 1/4 SSM
 *   4. Imposable = brut - cotisations SS (hors dep) - abattements (FFO + FDS + FD + AC)
 *   5. RTS = bareme progressif sur l'imposable
 *   6. Net = brut - cotisations SS totales - RTS
 *   7. A verser = net + CIS + CIC + CICO2 - avantages en nature + cheques-repas
 *
 * Regles :
 * - Tous les montants en CENTIMES (integer)
 * - Arrondi bancaire (Math.round)
 * - Credits d'impot ajoutes APRES le net (pas deduits de la RTS)
 */

import {
  COTISATIONS_SALARIALES,
  COTISATIONS_PATRONALES,
  PLAFOND_COTISABLE_CENTS,
  QUART_SSM_CENTS,
  CLASSES_MUTUALITE,
  FFO_MENSUEL_CENTS,
  FDS_MENSUEL_CENTS,
  AC_MENSUEL_CENTS,
  CIS_MENSUEL_CENTS,
  CIC_MENSUEL_CENTS,
  CIM_MENSUEL_CENTS,
  CICO2_MENSUEL_CENTS,
  MAJORATION_HEURES_SUP,
  HEURES_MENSUELLES_STANDARD,
  CHEQUE_REPAS_EXONERE_MAX_CENTS,
  type CompanyRatesConfig,
  DEFAULT_COMPANY_RATES,
} from '../../config/rates'

import { calculateMonthlyRTS, type TaxClass } from '../../config/rts'

// ─── Types ───────────────────────────────────────────────────────

export interface PayrollInput {
  /** Salaire brut de base mensuel en centimes */
  grossBaseCents: number
  /** Classe d'impot */
  taxClass: TaxClass
  /**
   * Taux de retenue personnalise ACD (en %, ex: 9.27)
   * Fourni par la fiche de retenue d'impot du salarie.
   * Si renseigne: RTS = imposable * taux / 100
   * Si null/undefined: fallback sur le bareme progressif (estimation)
   */
  rtsPersonalRate?: number
  /** Nombre d'heures supplementaires dans le mois */
  overtimeHours: number
  /** 13eme mois verse ce mois (en centimes, 0 si pas de versement) */
  thirteenthMonthCents: number
  /** Nombre de cheques-repas */
  mealVouchersCount: number
  /** Valeur faciale du cheque-repas en centimes */
  mealVoucherValueCents: number
  /** Avantage en nature voiture en centimes/mois */
  carBenefitCents: number
  /** Avantage en nature logement en centimes/mois */
  housingBenefitCents: number
  /** Configuration taux employeur (accident + mutualite + FD) */
  companyRates?: CompanyRatesConfig
}

export interface PayrollResult {
  // ─── Composantes du brut
  grossBaseCents: number
  overtimeCents: number
  thirteenthMonthCents: number
  benefitsInKindCents: number
  grossTotalCents: number

  // ─── Cotisations salariales
  cappedGrossCents: number
  ssEmployeeMaladieCents: number
  ssEmployeePensionCents: number
  /** Assiette dependance = brut - 1/4 SSM */
  dependanceBaseCents: number
  ssEmployeeDependanceCents: number
  ssEmployeeTotalCents: number

  // ─── Abattements fiscaux
  abatementFFOCents: number
  abatementFDSCents: number
  abatementFDCents: number
  abatementACCents: number
  totalAbatementsCents: number

  // ─── Impot
  /** Imposable = brut - cotisations SS hors dep - abattements */
  taxableIncomeCents: number
  rtsCents: number
  /** true si taux personnalise ACD utilise, false si bareme estimatif */
  rtsIsPersonalized: boolean

  // ─── Net
  netCents: number

  // ─── Credits d'impot (ajoutes APRES le net)
  creditCISCents: number
  creditCICCents: number
  creditCIMCents: number
  creditCICO2Cents: number
  totalCreditsCents: number

  // ─── A verser
  /** A verser = net + credits - avantages en nature + cheques-repas */
  aVerserCents: number

  // ─── Cheques-repas
  mealVouchersEmployerCents: number
  mealVouchersEmployeeCents: number

  // ─── Cout employeur
  ssEmployerTotalCents: number
  totalCostCents: number
}

// ─── Moteur ──────────────────────────────────────────────────────

export function calculatePayroll(input: PayrollInput): PayrollResult {
  const rates = input.companyRates ?? DEFAULT_COMPANY_RATES

  // ─── 1. Composantes du brut ────────────────────────────────

  const grossBase = input.grossBaseCents
  const hourlyRate = Math.round(grossBase / HEURES_MENSUELLES_STANDARD)
  const overtimeCents = Math.round(
    input.overtimeHours * hourlyRate * (1 + MAJORATION_HEURES_SUP / 100),
  )
  const thirteenthMonth = input.thirteenthMonthCents
  const benefitsInKind = input.carBenefitCents + input.housingBenefitCents
  const grossTotal = grossBase + overtimeCents + thirteenthMonth + benefitsInKind

  // ─── 2. Cotisations SS (maladie + pension) sur brut plafonne

  const cappedGross = Math.min(grossTotal, PLAFOND_COTISABLE_CENTS)
  const ssMaladieEspeces = Math.round(cappedGross * COTISATIONS_SALARIALES.maladie_especes / 100)
  const ssMaladieSoins = Math.round(cappedGross * COTISATIONS_SALARIALES.maladie_soins / 100)
  const ssPension = Math.round(cappedGross * COTISATIONS_SALARIALES.pension / 100)
  const ssMaladie = ssMaladieEspeces + ssMaladieSoins

  // Cotisations SS hors dependance
  const ssHorsDep = ssMaladie + ssPension

  // ─── 3. Dependance : assiette = brut - 1/4 SSM ────────────

  const dependanceBase = Math.max(0, grossTotal - QUART_SSM_CENTS)
  const ssDependance = Math.round(dependanceBase * COTISATIONS_SALARIALES.dependance / 100)

  const ssEmployeeTotal = ssHorsDep + ssDependance

  // ─── 4. Abattements fiscaux ────────────────────────────────

  const abFFO = FFO_MENSUEL_CENTS
  const abFDS = FDS_MENSUEL_CENTS
  const abFD = rates.fraisDeplacementCents
  const abAC = input.taxClass === '2' ? AC_MENSUEL_CENTS : 0
  const totalAbatements = abFFO + abFDS + abFD + abAC

  // Imposable = brut - cotisations SS hors dep - abattements
  const taxableIncome = Math.max(0, grossTotal - ssHorsDep - totalAbatements)

  // ─── 5. RTS ─────────────────────────────────────────────────
  //   Si taux personnalise ACD : RTS = imposable * taux
  //   Sinon : fallback bareme progressif (estimation)

  const rts = input.rtsPersonalRate != null
    ? Math.round(taxableIncome * input.rtsPersonalRate / 100)
    : calculateMonthlyRTS(taxableIncome, input.taxClass)

  // ─── 6. Net = brut - cotisations SS totales - RTS ──────────

  const net = grossTotal - ssEmployeeTotal - rts

  // ─── 7. Credits d'impot (ajoutes APRES le net) ─────────────

  const creditCIS = CIS_MENSUEL_CENTS
  const creditCIC = input.taxClass === '2' ? CIC_MENSUEL_CENTS : 0
  const creditCIM = input.taxClass === '1a' ? CIM_MENSUEL_CENTS : 0
  const creditCICO2 = CICO2_MENSUEL_CENTS
  const totalCredits = creditCIS + creditCIC + creditCIM + creditCICO2

  // ─── 8. Cheques-repas ──────────────────────────────────────

  const mealVoucherExempt = Math.min(input.mealVoucherValueCents, CHEQUE_REPAS_EXONERE_MAX_CENTS)
  const mealVouchersEmployer = input.mealVouchersCount * mealVoucherExempt
  const mealVouchersEmployee = input.mealVouchersCount *
    Math.max(0, input.mealVoucherValueCents - mealVoucherExempt)

  // ─── 9. A verser = net + credits - avantages + cheques ─────

  const aVerser = net + totalCredits - benefitsInKind + mealVouchersEmployer

  // ─── 10. Cotisations patronales ────────────────────────────

  const mutualiteRate = CLASSES_MUTUALITE[rates.mutualiteClass] ?? 0.69
  const ssEmployerMaladie = Math.round(
    cappedGross * (COTISATIONS_PATRONALES.maladie_especes + COTISATIONS_PATRONALES.maladie_soins) / 100,
  )
  const ssEmployerPension = Math.round(cappedGross * COTISATIONS_PATRONALES.pension / 100)
  const ssEmployerAccident = Math.round(cappedGross * rates.accidentTravailRate / 100)
  const ssEmployerMutualite = Math.round(cappedGross * mutualiteRate / 100)
  const ssEmployerSante = Math.round(cappedGross * COTISATIONS_PATRONALES.sante_travail / 100)
  const ssEmployerTotal =
    ssEmployerMaladie + ssEmployerPension + ssEmployerAccident +
    ssEmployerMutualite + ssEmployerSante
  const totalCost = grossTotal + ssEmployerTotal + mealVouchersEmployer

  // ─── Resultat ──────────────────────────────────────────────

  return {
    grossBaseCents: grossBase,
    overtimeCents,
    thirteenthMonthCents: thirteenthMonth,
    benefitsInKindCents: benefitsInKind,
    grossTotalCents: grossTotal,

    cappedGrossCents: cappedGross,
    ssEmployeeMaladieCents: ssMaladie,
    ssEmployeePensionCents: ssPension,
    dependanceBaseCents: dependanceBase,
    ssEmployeeDependanceCents: ssDependance,
    ssEmployeeTotalCents: ssEmployeeTotal,

    abatementFFOCents: abFFO,
    abatementFDSCents: abFDS,
    abatementFDCents: abFD,
    abatementACCents: abAC,
    totalAbatementsCents: totalAbatements,

    taxableIncomeCents: taxableIncome,
    rtsCents: rts,
    rtsIsPersonalized: input.rtsPersonalRate != null,

    netCents: net,

    creditCISCents: creditCIS,
    creditCICCents: creditCIC,
    creditCIMCents: creditCIM,
    creditCICO2Cents: creditCICO2,
    totalCreditsCents: totalCredits,

    aVerserCents: aVerser,

    mealVouchersEmployerCents: mealVouchersEmployer,
    mealVouchersEmployeeCents: mealVouchersEmployee,

    ssEmployerTotalCents: ssEmployerTotal,
    totalCostCents: totalCost,
  }
}

// ─── Helper: formatage ──────────────────────────────────────────

export function centsToEur(cents: number): string {
  const eur = cents / 100
  return eur.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}
